import React, { ReactNode, useState } from 'react';
import { Accordion as MantineAccordion } from '@mantine/core';
import clsx from 'clsx';
import './Accordion.scss';

export type IAccordion = {
    /** Заголовок */
    title: ReactNode;
    /** Контент */
    content: ReactNode;
    /** Открыт по умолчанию
     * @default false
     */
    defaultOpened?: boolean;
    /** Заблокирован выбор или нет
     * @default false
     */
    disabled?: boolean;
};

export interface IAccordionProps {
    /** Массив данных */
    data: IAccordion[];
    /** Позволяет разворачивать сразу несколько элементов
     * @default false
     */
    multiple?: boolean;
    /** Эффект наведения на элемент
     * @default true
     */
    hoverable?: boolean;

    /** Пункт раскрыт или свёрнут щелчком: индекс в `data` и новое состояние */
    onItemToggle?: (index: number, opened: boolean) => void;
}

// Фасад на Mantine Accordion. Публичный IAccordionProps: data, multiple (как у Mantine),
// hoverable, onItemToggle. Слоты замаплены на легаси-BEM: item→__item-wrap,
// control→__item, label→__item-inner, chevron→__item-icon, panel/content — как были;
// внешние SCSS-консьюмеры (ErrorPage/DebugPanel/SearchDocsExtraFilter) живут на тех же
// классах. Контролируемый value: модификатор --opened вешаем сами (его ждут тесты и
// внешние стили) параллельно Mantine data-active. Самодельный listbox-DOM (role=tree,
// заглушенный keydown) заменён нативным Accordion (кнопки, Enter/Space, стрелки — a11y).
const CLASS_NAMES = {
    root: 'rf-accordion',
    item: 'rf-accordion__item-wrap',
    control: 'rf-accordion__item',
    label: 'rf-accordion__item-inner',
    chevron: 'rf-accordion__item-icon',
    panel: 'rf-accordion__panel',
    content: 'rf-accordion__content',
} as const;

const Accordion: React.FC<IAccordionProps> = ({
    data = [],
    multiple = false,
    hoverable = true,
    onItemToggle = undefined,
}: IAccordionProps) => {
    // Легаси-семантика defaultOpened: в single-режиме уважается только ПЕРВЫЙ помеченный.
    const [opened, setOpened] = useState<string[]>(() =>
        data.reduce((acc: string[], item, index) => {
            if (item.defaultOpened && (multiple || !acc.length)) acc.push(String(index));
            return acc;
        }, []),
    );

    // onItemToggle — только по кликнутому элементу (легаси не сообщал об автозакрытии
    // соседа в single-режиме; Mantine в onChange отдаёт весь value, кликнутый — это дифф).
    const onChangeSingle = (value: string | null) => {
        if (onItemToggle) {
            if (value != null) onItemToggle(Number(value), true);
            else if (opened.length) onItemToggle(Number(opened[0]), false);
        }
        setOpened(value == null ? [] : [value]);
    };

    const onChangeMultiple = (value: string[]) => {
        if (onItemToggle) {
            const added = value.find((v) => !opened.includes(v));
            const removed = opened.find((v) => !value.includes(v));
            if (added != null) onItemToggle(Number(added), true);
            if (removed != null) onItemToggle(Number(removed), false);
        }
        setOpened(value);
    };

    const items = data.map((item, index) => (
        <MantineAccordion.Item
            key={index}
            value={String(index)}
            className={clsx(
                opened.includes(String(index)) && 'rf-accordion__item-wrap--opened',
                hoverable && 'rf-accordion__item-wrap--hoverable',
                item.disabled && 'rf-accordion__item-wrap--disabled',
            )}
        >
            <MantineAccordion.Control disabled={item.disabled}>{item.title}</MantineAccordion.Control>
            <MantineAccordion.Panel>{item.content}</MantineAccordion.Panel>
        </MantineAccordion.Item>
    ));

    const shared = {
        classNames: CLASS_NAMES,
        chevronPosition: 'right' as const,
    };

    return multiple ? (
        <MantineAccordion multiple value={opened} onChange={onChangeMultiple} {...shared}>
            {items}
        </MantineAccordion>
    ) : (
        <MantineAccordion value={opened[0] ?? null} onChange={onChangeSingle} {...shared}>
            {items}
        </MantineAccordion>
    );
};

export default Accordion;
